import type {ReactNode, SVGProps} from 'react';
import React from 'react';

export type StudioIconProps = Omit<SVGProps<SVGSVGElement>, 'color'> & {
	readonly color?: string;
	readonly size?: number;
};

type SvgIconProps = StudioIconProps & {
	readonly children: ReactNode;
	readonly viewBox: string;
};

const SvgIcon: React.FC<SvgIconProps> = ({
	children,
	color = 'currentColor',
	size = 18,
	viewBox,
	...svgProps
}) => {
	return (
		<svg
			aria-hidden="true"
			focusable="false"
			height={size}
			width={size}
			viewBox={viewBox}
			color={color}
			xmlns="http://www.w3.org/2000/svg"
			{...svgProps}
		>
			{children}
		</svg>
	);
};

export const RemotionGlyph: React.FC<StudioIconProps> = (props) => {
	return (
		<SvgIcon viewBox="0 0 415 426" {...props}>
			<path
				fill="currentColor"
				d="M93.4691 0.0367432C84.4873 0.520935 77.2494 1.93634 69.9553 4.69266C66.3176 6.05219 60.3548 9.0134 57.0734 11.062C43.3476 19.6103 32.8846 32.4606 27.428 47.4154C26.3405 50.3766 23.3966 59.8188 21.5027 66.3185C8.88329 109.768 1.7204 157.277 0.182822 207.561C-0.0609408 215.569 -0.0609408 234.639 0.182822 242.517C1.21413 275.854 4.42055 305.949 10.2334 336.641C12.596 349.063 16.3837 365.75 18.5776 373.33C23.059 388.732 32.2095 401.843 45.2227 411.453C53.9419 417.896 63.8425 422.217 74.8118 424.34C80.0996 425.365 87.075 425.83 92.2127 425.495C99.3194 425.029 113.42 423.148 124.877 421.118C176.517 411.974 224.22 395.604 267.478 372.175C294.874 357.332 318.294 341.26 340.888 321.761C363.408 302.355 382.609 281.478 399.504 258.049C403.423 252.63 405.392 249.464 407.361 245.478C412.424 235.198 414.805 224.974 414.786 213.539C414.786 202.886 412.761 193.425 408.392 183.741C406.292 179.066 404.286 175.714 399.785 169.345C383.21 145.898 364.815 125.467 342.389 105.614C307.624 74.8481 266.335 49.613 220.226 30.9334C210.232 26.8921 200.387 23.335 188.537 19.4799C163.448 11.3413 132.396 4.28293 106.126 0.763062C102.001 0.204346 96.3942 -0.112244 93.4691 0.0367432Z"
			/>
		</SvgIcon>
	);
};

export type SidebarIconProps = StudioIconProps & {
	readonly expanded?: boolean;
	readonly side: 'left' | 'right';
};

export const SidebarIcon: React.FC<SidebarIconProps> = ({
	expanded = true,
	side,
	style,
	...props
}) => {
	const dividerX = side === 'left' ? 5.75 : 10.25;

	return (
		<SvgIcon
			viewBox="0 0 16 16"
			style={{overflow: 'visible', ...style}}
			{...props}
		>
			<rect
				x="0.5"
				y="0.5"
				width="15"
				height="15"
				rx="2.5"
				fill="none"
				stroke="currentColor"
			/>
			{expanded ? (
				<path
					d={side === 'left' ? 'M1 1h4.75v14H1z' : 'M10.25 1H15v14h-4.75z'}
					fill="currentColor"
				/>
			) : null}
			<path d={`M${dividerX} 1v14`} fill="none" stroke="currentColor" />
		</SvgIcon>
	);
};

export type FolderIconProps = StudioIconProps & {
	readonly expanded?: boolean;
};

export const FolderIcon: React.FC<FolderIconProps> = ({
	expanded = true,
	...props
}) => {
	return (
		<SvgIcon viewBox={expanded ? '0 0 576 512' : '0 0 512 512'} {...props}>
			<path
				fill="currentColor"
				d={
					expanded
						? 'M566.6 211.6C557.5 199.1 543.4 192 527.1 192H134.2C114.3 192 96.2 204.5 89.23 223.1L32 375.8V96c0-17.64 14.36-32 32-32h117.5c8.549 0 16.58 3.328 22.63 9.375L258.7 128H448c17.64 0 32 14.36 32 32h32c0-35.35-28.65-64-64-64H272L226.7 50.75C214.7 38.74 198.5 32 181.5 32H64C28.65 32 0 60.65 0 96v320c0 35.35 28.65 64 64 64h403.1c21.11 0 39.53-13.53 45.81-33.69l60-192C578.4 239.6 575.8 224 566.6 211.6zM543.2 244.8l-60 192C481.1 443.5 475 448 467.1 448H64c-3.322 0-6.357-.9551-9.373-1.898-2.184-1.17-4.109-2.832-5.596-4.977-3.031-4.375-3.703-9.75-1.828-14.73l72-192C121.5 228.2 127.5 224 134.2 224h393.8c5.141 0 9.844 2.375 12.89 6.516C543.9 234.7 544.8 239.9 543.2 244.8z'
						: 'M447.1 96H272L226.7 50.75C214.7 38.74 198.5 32 181.5 32H63.1c-35.35 0-64 28.65-64 64v320c0 35.35 28.65 64 64 64h384c35.35 0 64-28.65 64-64V160C511.1 124.7 483.3 96 447.1 96zM480 416c0 17.64-14.36 32-32 32H64c-17.64 0-32-14.36-32-32V96c0-17.64 14.36-32 32-32h117.5c8.549 0 16.58 3.328 22.63 9.375L258.7 128H448c17.64 0 32 14.36 32 32V416z'
				}
			/>
		</SvgIcon>
	);
};

export const SearchIcon: React.FC<StudioIconProps> = (props) => {
	return (
		<SvgIcon viewBox="0 0 640 640" {...props}>
			<path
				fill="currentColor"
				d="M432 272C432 183.6 360.4 112 272 112C183.6 112 112 183.6 112 272C112 360.4 183.6 432 272 432C360.4 432 432 360.4 432 272zM401.1 435.1C365.7 463.2 320.8 480 272 480C157.1 480 64 386.9 64 272C64 157.1 157.1 64 272 64C386.9 64 480 157.1 480 272C480 320.8 463.2 365.7 435.1 401.1L569 535C578.4 544.4 578.4 559.6 569 568.9C559.6 578.2 544.4 578.3 535.1 568.9L401.1 435.1z"
			/>
		</SvgIcon>
	);
};

export const CanvasFitIcon: React.FC<StudioIconProps> = (props) => {
	return (
		<SvgIcon viewBox="0 0 640 640" {...props}>
			<path
				fill="currentColor"
				d="M448 272C448 174.8 369.2 96 272 96C174.8 96 96 174.8 96 272C96 369.2 174.8 448 272 448C369.2 448 448 369.2 448 272zM407.3 430C371 461.2 323.7 480 272 480C157.1 480 64 386.9 64 272C64 157.1 157.1 64 272 64C386.9 64 480 157.1 480 272C480 323.7 461.2 371 430 407.3L571.3 548.7C577.5 554.9 577.5 565.1 571.3 571.3C565.1 577.5 554.9 577.5 548.7 571.3L407.3 430z"
			/>
		</SvgIcon>
	);
};

export const CanvasZoomIcon: React.FC<StudioIconProps> = (props) => {
	return (
		<SvgIcon viewBox="0 0 640 640" {...props}>
			<path
				fill="currentColor"
				d="M272 96C369.2 96 448 174.8 448 272C448 369.2 369.2 448 272 448C174.8 448 96 369.2 96 272C96 174.8 174.8 96 272 96zM272 480C323.7 480 371 461.2 407.3 430L548.7 571.3C554.9 577.5 565.1 577.5 571.3 571.3C577.5 565.1 577.5 554.9 571.3 548.7L430 407.3C461.2 371 480 323.7 480 272C480 157.1 386.9 64 272 64C157.1 64 64 157.1 64 272C64 386.9 157.1 480 272 480zM256 344C256 352.8 263.2 360 272 360C280.8 360 288 352.8 288 344L288 288L344 288C352.8 288 360 280.8 360 272C360 263.2 352.8 256 344 256L288 256L288 200C288 191.2 280.8 184 272 184C263.2 184 256 191.2 256 200L256 256L200 256C191.2 256 184 263.2 184 272C184 280.8 191.2 288 200 288L256 288L256 344z"
			/>
		</SvgIcon>
	);
};

export const CanvasZoomOutIcon: React.FC<StudioIconProps> = (props) => {
	return (
		<SvgIcon viewBox="0 0 640 640" {...props}>
			<path
				fill="currentColor"
				d="M272 96C369.2 96 448 174.8 448 272C448 369.2 369.2 448 272 448C174.8 448 96 369.2 96 272C96 174.8 174.8 96 272 96zM272 480C323.7 480 371 461.2 407.3 430L548.7 571.3C554.9 577.5 565.1 577.5 571.3 571.3C577.5 565.1 577.5 554.9 571.3 548.7L430 407.3C461.2 371 480 323.7 480 272C480 157.1 386.9 64 272 64C157.1 64 64 157.1 64 272C64 386.9 157.1 480 272 480zM192 256C183.2 256 176 263.2 176 272C176 280.8 183.2 288 192 288L352 288C360.8 288 368 280.8 368 272C368 263.2 360.8 256 352 256L192 256z"
			/>
		</SvgIcon>
	);
};

export const PlaybackRateIcon: React.FC<StudioIconProps> = (props) => {
	return (
		<SvgIcon viewBox="0 0 640 640" {...props}>
			<path
				fill="currentColor"
				d="M320 96C443.7 96 544 196.3 544 320C544 443.7 443.7 544 320 544C196.3 544 96 443.7 96 320C96 196.3 196.3 96 320 96zM320 576C461.4 576 576 461.4 576 320C576 178.6 461.4 64 320 64C178.6 64 64 178.6 64 320C64 461.4 178.6 576 320 576zM320 384C337.7 384 352 398.3 352 416C352 433.7 337.7 448 320 448C302.3 448 288 433.7 288 416C288 398.3 302.3 384 320 384zM320 480C355.3 480 384 451.3 384 416C384 386.2 363.6 361.1 336 354L336 160C336 151.2 328.8 144 320 144C311.2 144 304 151.2 304 160L304 354C276.4 361.1 256 386.2 256 416C256 451.3 284.7 480 320 480zM208 232C221.3 232 232 221.3 232 208C232 194.7 221.3 184 208 184C194.7 184 184 194.7 184 208C184 221.3 194.7 232 208 232zM184 320C184 306.7 173.3 296 160 296C146.7 296 136 306.7 136 320C136 333.3 146.7 344 160 344C173.3 344 184 333.3 184 320zM480 344C493.3 344 504 333.3 504 320C504 306.7 493.3 296 480 296C466.7 296 456 306.7 456 320C456 333.3 466.7 344 480 344zM456 208C456 194.7 445.3 184 432 184C418.7 184 408 194.7 408 208C408 221.3 418.7 232 432 232C445.3 232 456 221.3 456 208z"
			/>
		</SvgIcon>
	);
};

export const SettingsIcon: React.FC<StudioIconProps> = (props) => {
	return (
		<SvgIcon viewBox="0 0 512 512" {...props}>
			<path
				fill="currentColor"
				d="M168 255.1c0-47.7 39.4-88 88-88s88 40.3 88 88c0 49.5-39.4 88.9-88 88.9s-88-39.4-88-88.9zm88-56c-30.9 0-56 26-56 56 0 31.8 25.1 56 56 56s56-24.2 56-56c0-30-25.1-56-56-56zM65.67 230.6l-40.33-36.8c-11.12-10.1-13.68-26.6-6.16-39.6l30.24-52.4c7.52-13.02 23.09-19.05 37.42-14.48l51.96 16.58c13.4-10.34 28.2-18.94 44-25.47l11.7-53.27C197.7 10.47 210.7 0 225.8 0h60.4c15.1 0 28.1 10.47 31.3 25.16l11.7 53.27c14.9 6.53 30.6 15.13 44 25.47l52-16.58c14.3-4.57 29.9 1.46 37.4 14.48l30.2 52.4c7.5 13 5 29.5-6.1 39.6l-40.4 36.8c1.1 8.3 1.7 16.8 1.7 24.5 0 9.5-.6 18-1.7 26.3l40.4 36.8c11.1 10.1 13.6 26.6 6.1 39.6l-30.2 52.4c-7.5 13-23.1 19-37.4 14.5l-52-16.6c-13.4 10.3-29.1 18.9-44 25.5l-11.7 53.2c-3.2 14.7-16.2 25.2-31.3 25.2h-60.4c-15.1 0-28.1-10.5-31.3-25.2l-11.7-53.2c-15.8-6.6-30.6-15.2-44-25.5l-51.96 16.6c-14.33 4.5-29.9-1.5-37.42-14.5l-30.24-52.4c-7.52-13-4.96-29.5 6.16-39.6l40.33-36.8c-1.1-8.3-1.67-16.8-1.67-26.3 0-7.7.57-16.2 1.67-24.5zm92.73-101.4-13.3 10.3-67.97-21.7-30.24 52.4 52.69 48-2.19 16.6c-.92 6.9-1.39 14-1.39 20.3 0 8.1.47 15.2 1.39 22.1l2.19 16.6-52.69 48 30.24 52.4 67.97-21.7 13.3 10.3c11.1 8.6 23.5 15.8 36.6 20.3l15.5 7.3 15.3 69.6h60.4l15.3-69.6 14.6-7.3c14-4.5 26.4-11.7 37.5-20.3l13.3-10.3 68 21.7 30.2-52.4-52.7-48 2.2-16.6c.9-6.9 1.4-14 1.4-21.2 0-7.2-.5-14.3-1.4-21.2l-2.2-16.6 52.7-48-30.2-52.4-68 21.7-13.3-10.3c-11.1-8.6-23.5-15.8-37.5-21.2l-14.6-6.4L286.2 32h-60.4l-15.3 69.6L195 108c-13.1 5.4-25.5 12.6-36.6 21.2z"
			/>
		</SvgIcon>
	);
};

export const VscodeIcon: React.FC<StudioIconProps> = ({
	color = '#23a9f2',
	...props
}) => {
	return (
		<SvgIcon color={color} viewBox="0 0 40 40" {...props}>
			<path
				clipRule="evenodd"
				fill="currentColor"
				fillRule="evenodd"
				d="M26.02 34.13c.23.09.48.13.72.12.25-.01.49-.07.71-.18l5.93-2.85c.31-.15.56-.38.74-.66.18-.29.28-.62.28-.96V10.25c0-.34-.1-.67-.28-.96-.18-.28-.43-.51-.74-.66l-5.93-2.85c-.34-.16-.71-.22-1.08-.15-.36.06-.7.23-.96.5L14.05 16.48 9.11 12.73a1.2 1.2 0 0 0-1.53.07L5.99 14.24a1.2 1.2 0 0 0 0 1.77l4.29 3.92-4.29 3.91a1.2 1.2 0 0 0 0 1.77l1.59 1.44a1.2 1.2 0 0 0 1.53.07l4.94-3.75 11.36 10.36c.17.17.38.31.61.4Zm1.18-20.74-8.61 6.54 8.61 6.53V13.39Z"
			/>
		</SvgIcon>
	);
};

export const BracesIcon: React.FC<StudioIconProps> = (props) => {
	return (
		<SvgIcon viewBox="0 0 576 512" {...props}>
			<path
				fill="currentColor"
				d="M144 32c-44.2 0-80 35.8-80 80v53.5c0 12.7-5.1 24.9-14.1 33.9L4.7 244.7c-3 3-4.7 7.1-4.7 11.3s1.7 8.3 4.7 11.3L50 312.6c9 9 14.1 21.2 14.1 33.9V400c0 44.2 35.8 80 80 80h64c8.8 0 16-7.2 16-16s-7.2-16-16-16h-64c-26.5 0-48-21.5-48-48v-53.5c0-21.2-8.4-41.6-23.4-56.6L38.6 256l34-33.9c15-15 23.4-35.4 23.4-56.6V112c0-26.5 21.5-48 48-48h64c8.8 0 16-7.2 16-16s-7.2-16-16-16h-64zm288 0h-64c-8.8 0-16 7.2-16 16s7.2 16 16 16h64c26.5 0 48 21.5 48 48v53.5c0 21.2 8.4 41.6 23.4 56.6l33.9 33.9-33.9 33.9c-15 15-23.4 35.4-23.4 56.6V400c0 26.5-21.5 48-48 48h-64c-8.8 0-16 7.2-16 16s7.2 16 16 16h64c44.2 0 80-35.8 80-80v-53.5c0-12.7 5.1-24.9 14.1-33.9l45.3-45.3c3-3 4.7-7.1 4.7-11.3s-1.7-8.3-4.7-11.3l-45.3-45.3c-9-9-14.1-21.2-14.1-33.9V112c0-44.2-35.8-80-80-80z"
			/>
		</SvgIcon>
	);
};

export const JumpToStartIcon: React.FC<StudioIconProps> = (props) => {
	return (
		<SvgIcon viewBox="0 0 512 512" {...props}>
			<path
				fill="currentColor"
				d="M0 415.1V96.03c0-17.67 14.33-31.1 31.1-31.1C49.67 64.03 64 78.36 64 96.03v131.8l171.5-156.5C256.1 54.28 288 68.66 288 96.03v131.9l171.5-156.5C480.1 54.28 512 68.66 512 96.03v319.9c0 27.37-31.88 41.74-52.5 24.62L288 285.2v130.7c0 27.37-31.88 41.74-52.5 24.62L64 285.2v130.7c0 17.67-14.33 31.1-31.1 31.1C14.33 447.1 0 433.6 0 415.1z"
			/>
		</SvgIcon>
	);
};

export const StepBackIcon: React.FC<StudioIconProps> = (props) => {
	return (
		<SvgIcon viewBox="0 0 448 512" {...props}>
			<path
				fill="currentColor"
				d="M64 468V44c0-6.6 5.4-12 12-12h48c6.6 0 12 5.4 12 12v176.4l195.5-181C352.1 22.3 384 36.6 384 64v384c0 27.4-31.9 41.7-52.5 24.6L136 292.7V468c0 6.6-5.4 12-12 12H76c-6.6 0-12-5.4-12-12z"
			/>
		</SvgIcon>
	);
};

export const PlayIcon: React.FC<StudioIconProps> = (props) => {
	return (
		<SvgIcon viewBox="0 0 448 512" {...props}>
			<path
				fill="currentColor"
				d="M424.4 214.7L72.4 6.6C43.8-10.3 0 6.1 0 47.9V464c0 37.5 40.7 60.1 72.4 41.3l352-208c31.4-18.5 31.5-64.1 0-82.6z"
			/>
		</SvgIcon>
	);
};

export const StepForwardIcon: React.FC<StudioIconProps> = (props) => {
	return (
		<SvgIcon viewBox="0 0 448 512" {...props}>
			<path
				fill="currentColor"
				d="M384 44v424c0 6.6-5.4 12-12 12h-48c-6.6 0-12-5.4-12-12V291.6l-195.5 181C95.9 489.7 64 475.4 64 448V64c0-27.4 31.9-41.7 52.5-24.6L312 219.3V44c0-6.6 5.4-12 12-12h48c6.6 0 12 5.4 12 12z"
			/>
		</SvgIcon>
	);
};

export const LoopIcon: React.FC<StudioIconProps> = (props) => {
	return (
		<SvgIcon viewBox="0 0 512 512" {...props}>
			<path
				fill="currentColor"
				d="M384 150.3V41.6l64 53.3v2l-64 53.3zM352 23.5V80H192C86 80 0 166 0 272c0 8.8 7.2 16 16 16s16-7.2 16-16c0-88.4 71.6-160 160-160h160v56.5c0 13 10.5 23.5 23.5 23.5 5.5 0 10.8-1.9 15-5.4l78-65c7.3-6.1 11.5-15.1 11.5-24.6v-2c0-9.5-4.2-18.5-11.5-24.6l-78-65c-4.2-3.5-9.5-5.4-15-5.4-13 0-23.5 10.5-23.5 23.5zM128 415.9v54.5l-64-53.3v-2l64-53.3v54.1zM160 400v-56.5c0-13-10.5-23.5-23.5-23.5-5.5 0-10.8 1.9-15 5.4l-78 65C36.2 396.5 32 405.5 32 415v2c0 9.5 4.2 18.5 11.5 24.6l78 65c4.2 3.5 9.5 5.4 15 5.4 13 0 23.5-10.5 23.5-23.5V432h160c106 0 192-86 192-192 0-8.8-7.2-16-16-16s-16 7.2-16 16c0 88.4-71.6 160-160 160H160z"
			/>
		</SvgIcon>
	);
};

const speakerPath =
	'M112 320H48c-8.8 0-16-7.2-16-16v-96c0-8.8 7.2-16 16-16h64c8.5 0 16.6-3.4 22.6-9.4L252.7 64.6c.4-.4.9-.6 1.4-.6 1.1 0 1.9.9 1.9 1.9V446c0 1.1-.9 1.9-1.9 1.9-.5 0-1-.2-1.4-.6L134.6 329.4c-6-6-14.1-9.4-22.6-9.4zm0-160H48c-26.5 0-48 21.5-48 48v96c0 26.5 21.5 48 48 48h64l118.1 118.1c6.4 6.4 15 9.9 24 9.9 18.7 0 33.9-15.2 33.9-33.9V65.9C288 47.2 272.8 32 254.1 32c-9 0-17.6 3.6-24 9.9L112 160zm243.2 22.4c-5.3 7.1-3.9 17.1 3.2 22.4 15.6 11.7 25.6 30.3 25.6 51.2s-10 39.5-25.6 51.2c-7.1 5.3-8.5 15.3-3.2 22.4s15.3 8.5 22.4 3.2C400.9 315.3 416 287.4 416 256s-15.1-59.3-38.4-76.8c-7.1-5.3-17.1-3.9-22.4 3.2zm87-74.5c-6.8-5.6-16.9-4.7-22.5 2.1s-4.7 16.9 2.1 22.5C457.4 161.9 480 206.3 480 256s-22.6 94.1-58.2 123.4c-6.8 5.6-7.8 15.7-2.1 22.5s15.7 7.8 22.5 2.2C484.8 368.9 512 315.6 512 256s-27.2-112.9-69.8-148.1z';

export type VolumeIconProps = StudioIconProps & {
	readonly muted?: boolean;
};

export const VolumeIcon: React.FC<VolumeIconProps> = ({
	muted = false,
	...props
}) => {
	return (
		<SvgIcon viewBox="0 0 576 512" {...props}>
			<g transform="translate(32 0)">
				<path d={speakerPath} fill="currentColor" />
			</g>
			{muted ? (
				<path
					d="M16-16 560 528"
					fill="none"
					stroke="currentColor"
					strokeLinecap="round"
					strokeWidth="32"
				/>
			) : null}
		</SvgIcon>
	);
};

export const InPointIcon: React.FC<StudioIconProps> = (props) => {
	return (
		<SvgIcon viewBox="0 0 16 16" {...props}>
			<path
				d="M9.5 1.5H5.5v13h4"
				fill="none"
				stroke="currentColor"
				strokeWidth="1"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</SvgIcon>
	);
};

export const OutPointIcon: React.FC<StudioIconProps> = (props) => {
	return (
		<SvgIcon viewBox="0 0 16 16" {...props}>
			<path
				d="M6.5 1.5h4v13H6.5"
				fill="none"
				stroke="currentColor"
				strokeWidth="1"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</SvgIcon>
	);
};

export const CheckerboardIcon: React.FC<StudioIconProps> = (props) => {
	return (
		<SvgIcon viewBox="0 0 18 18" {...props}>
			<path
				fill="currentColor"
				fillRule="evenodd"
				d="M3 2h12a1 1 0 0 1 1 1v12a1 1 0 0 1 -1 1H3a1 1 0 0 1 -1 -1V3a1 1 0 0 1 1 -1zM3 3h6v6H3zM9 9h6v6H9z"
			/>
		</SvgIcon>
	);
};

export const OutlineIcon: React.FC<StudioIconProps> = (props) => {
	return (
		<SvgIcon viewBox="0 0 512 512" {...props}>
			<path
				d="M64 64H448V448H64V64Z"
				fill="none"
				stroke="currentColor"
				strokeWidth="36"
			/>
			{[
				[16, 16],
				[400, 16],
				[16, 400],
				[400, 400],
			].map(([x, y]) => {
				return (
					<rect
						key={`${x}-${y}`}
						x={x}
						y={y}
						width="96"
						height="96"
						rx="24"
						fill="#1f2428"
						stroke="currentColor"
						strokeWidth="32"
					/>
				);
			})}
		</SvgIcon>
	);
};

export const RulerIcon: React.FC<StudioIconProps> = (props) => {
	return (
		<SvgIcon viewBox="0 0 576 512" {...props}>
			<path
				fill="currentColor"
				d="M525.9 154.2L186.5 493.6c-6.2 6.2-16.4 6.2-22.6 0L50.7 380.5c-6.2-6.2-6.2-16.4 0-22.6l33.9-33.9 56.6 56.6c6.2 6.2 16.4 6.2 22.6 0s6.2-16.4 0-22.6l-56.6-56.6 39.6-39.6 33.9 33.9c6.2 6.2 16.4 6.2 22.6 0s6.2-16.4 0-22.6l-33.9-33.9 39.6-39.6 56.6 56.6c6.2 6.2 16.4 6.2 22.6 0s6.2-16.4 0-22.6l-56.6-56.6 39.6-39.6 33.9 33.9c6.2 6.2 16.4 6.2 22.6 0s6.2-16.4 0-22.6l-33.9-33.9 39.6-39.6 56.6 56.6c6.2 6.2 16.4 6.2 22.6 0s6.2-16.4 0-22.6l-56.6-56.6 33.9-33.9c6.2-6.2 16.4-6.2 22.6 0L525.9 131.5c6.2 6.2 6.2 16.4 0 22.6zM73.3 289.9L28.1 335.2c-18.7 18.7-18.7 49.1 0 67.9L141.2 516.2c18.7 18.7 49.1 18.7 67.9 0L548.5 176.8c18.7-18.7 18.7-49.1 0-67.9L435.4-4.2C416.6-23 386.2-23 367.5-4.2 77.1 286.2 140.3 223 73.3 289.9z"
			/>
		</SvgIcon>
	);
};

export const MagnetIcon: React.FC<StudioIconProps> = (props) => {
	return (
		<SvgIcon viewBox="0 0 18 18" {...props}>
			<path
				fill="currentColor"
				fillRule="evenodd"
				d="M1 9a8 8 0 0 1 16 0v6a1 1 0 0 1 -1 1h-3a1 1 0 0 1 -1 -1V9a3 3 0 0 0 -6 0v6a1 1 0 0 1 -1 1H2a1 1 0 0 1 -1 -1zM2 9v3h3V9a4 4 0 0 1 8 0v3h3V9a7 7 0 0 0 -14 0zM2 13v2h3v-2zM13 13v2h3v-2z"
			/>
		</SvgIcon>
	);
};

export const FullscreenIcon: React.FC<StudioIconProps> = (props) => {
	return (
		<SvgIcon viewBox="0 0 18 18" {...props}>
			<path
				d="M6.5 2.5H2.5v4M2.5 11.5v4h4M11.5 2.5h4v4M15.5 11.5v4h-4"
				fill="none"
				stroke="currentColor"
				strokeWidth="1"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</SvgIcon>
	);
};

export const RocketIcon: React.FC<StudioIconProps> = (props) => {
	return (
		<SvgIcon viewBox="0 0 512 512" {...props}>
			<path
				fill="currentColor"
				d="M188.9 372l-50.4-50.4c18.6-42.6 61.7-137.7 95.1-187C304.6 30.1 409 24.6 475.7 36.3c11.7 66.7 6.2 171.1-98.4 242-49.4 33.5-145.5 75.6-188.4 93.7zm-79.9-62.8c-5.2 11.9-2.5 25.7 6.7 34.9l50.7 50.7c9.1 9.1 22.7 11.9 34.5 6.9 6.5-2.7 14.3-6 23-9.8L224 496c0 5.5 2.9 10.7 7.6 13.6s10.6 3.2 15.6.7l101.5-50.7c21.7-10.8 35.4-33 35.4-57.2v-90.3c4-2.5 7.7-4.9 11.3-7.3C516.1 222.9 520.1 100.9 506.7 28.1c-2.1-11.6-11.2-20.6-22.8-22.8C411.1-8.1 289.1-4.1 207.2 116.7c-2.4 3.6-4.9 7.3-7.3 11.3h-90.2c-24.2 0-46.4 13.7-57.2 35.4L1.7 264.8c-2.5 5-2.2 10.8.7 15.6s8.1 7.6 13.6 7.6h102.5c-3.6 8-6.8 15.2-9.4 21.2zM256 470.1v-92.5c30.3-13.7 65.4-30.3 96-47v71.7c0 12.1-6.8 23.2-17.7 28.6L256 470.1zM109.7 160h71.5c-16.9 30.7-34 65.8-48.1 96H41.9L81 177.7c5.4-10.8 16.5-17.7 28.6-17.7zM392 144a24 24 0 1 1-48 0 24 24 0 1 1 48 0zM368 88a56 56 0 1 0 0 112 56 56 0 1 0 0-112z"
			/>
		</SvgIcon>
	);
};

export const EyeIcon: React.FC<StudioIconProps> = (props) => {
	return (
		<SvgIcon viewBox="0 0 24 16" {...props}>
			<path
				fill="currentColor"
				d="M24 7.551S19.748 16 12.015 16C4.835 16 0 7.551 0 7.551S4.446 0 12.015 0C19.709 0 24 7.551 24 7.551zM17 8c0-2.757-2.243-5-5-5S7 5.243 7 8s2.243 5 5 5 5-2.243 5-5z"
			/>
		</SvgIcon>
	);
};

export const VideoIcon: React.FC<StudioIconProps> = (props) => {
	return (
		<SvgIcon viewBox="0 0 512 512" {...props}>
			<path
				fill="currentColor"
				d="M448 32H64C28.65 32 0 60.65 0 96v320c0 35.35 28.65 64 64 64h384c35.35 0 64-28.65 64-64V96C512 60.65 483.3 32 448 32zM384 64v176H128V64h256zM32 96c0-17.64 14.36-32 32-32h32v80H32V96zm0 80h64v64H32v-64zm0 96h64v64H32v-64zm32 176c-17.64 0-32-14.36-32-32v-48h64v80H64zm64 0V272h256v176H128zm352-32c0 17.64-14.36 32-32 32h-32v-80h64v48zm0-80h-64v-64h64v64zm0-96h-64v-64h64v64zm0-96h-64V64h32c17.64 0 32 14.36 32 32v48z"
			/>
		</SvgIcon>
	);
};

export const ImageIcon: React.FC<StudioIconProps> = (props) => {
	return (
		<SvgIcon viewBox="0 0 512 512" {...props}>
			<path
				fill="currentColor"
				d="M144 288c0-61.9 50.1-112 112-112s112 50.1 112 112-50.1 112-112 112-112-50.1-112-112zm112-80c-44.2 0-80 35.8-80 80s35.8 80 80 80 80-35.8 80-80-35.8-80-80-80zM362.9 64.82L373.3 96H448c35.3 0 64 28.7 64 64v256c0 35.3-28.7 64-64 64H64c-35.35 0-64-28.7-64-64V160c0-35.3 28.65-64 64-64h74.7l10.4-31.18C155.6 45.22 173.9 32 194.6 32h122.8c20.7 0 39 13.22 45.5 32.82zM64 128c-17.67 0-32 14.3-32 32v256c0 17.7 14.33 32 32 32h384c17.7 0 32-14.3 32-32V160c0-17.7-14.3-32-32-32h-97.7l-17.7-53.06C330.4 68.41 324.3 64 317.4 64H194.6c-6.9 0-13 4.41-15.2 10.94L161.7 128H64z"
			/>
		</SvgIcon>
	);
};

export type CaretIconProps = StudioIconProps & {
	readonly direction?: 'down' | 'right';
};

export const CaretIcon: React.FC<CaretIconProps> = ({
	direction = 'down',
	...props
}) => {
	if (direction === 'right') {
		return (
			<SvgIcon viewBox="0 0 192 512" {...props}>
				<path
					fill="currentColor"
					d="M0 384.662V127.338c0-17.818 21.543-26.741 34.142-14.142l128.662 128.662c7.81 7.81 7.81 20.474 0 28.284L34.142 398.804C21.543 411.404 0 402.48 0 384.662z"
				/>
			</SvgIcon>
		);
	}

	return (
		<SvgIcon viewBox="0 0 448 512" {...props}>
			<path
				fill="currentColor"
				d="M235.3 411.3c-6.2 6.2-16.4 6.2-22.6 0l-208-208c-6.2-6.2-6.2-16.4 0-22.6s16.4-6.2 22.6 0L224 377.4l196.7-196.7c6.2-6.2 16.4-6.2 22.6 0s6.2 16.4 0 22.6l-208 208z"
			/>
		</SvgIcon>
	);
};

// Font Awesome Pro v7.3.1, Copyright 2026 Fonticons, Inc.
// https://fontawesome.com/license (Commercial License)
// Matches the original Studio icon.
export const DuplicateIcon: React.FC<StudioIconProps> = (props) => {
	return (
		<SvgIcon viewBox="0 0 640 640" {...props}>
			<path
				fill="currentColor"
				d="M352 544L128 544C110.3 544 96 529.7 96 512L96 288C96 270.3 110.3 256 128 256L176 256L176 224L128 224C92.7 224 64 252.7 64 288L64 512C64 547.3 92.7 576 128 576L352 576C387.3 576 416 547.3 416 512L416 464L384 464L384 512C384 529.7 369.7 544 352 544zM288 384C270.3 384 256 369.7 256 352L256 128C256 110.3 270.3 96 288 96L512 96C529.7 96 544 110.3 544 128L544 352C544 369.7 529.7 384 512 384L288 384zM224 352C224 387.3 252.7 416 288 416L512 416C547.3 416 576 387.3 576 352L576 128C576 92.7 547.3 64 512 64L288 64C252.7 64 224 92.7 224 128L224 352z"
			/>
		</SvgIcon>
	);
};

// Matches the original Studio icon.
export const TrashIcon: React.FC<StudioIconProps> = (props) => {
	return (
		<SvgIcon viewBox="0 0 448 512" {...props}>
			<path
				fill="currentColor"
				d="M160.5 27.4C162.5 20.6 168.8 16 175.8 16h96.4c7.1 0 13.3 4.6 15.3 11.4l11 36.6h-149l11-36.6zM116.1 64H16C7.2 64 0 71.2 0 80s7.2 16 16 16h416c8.8 0 16-7.2 16-16s-7.2-16-16-16H331.9l-13.7-45.8C312.1-2.1 293.4-16 272.2-16h-96.4c-21.2 0-39.9 13.9-46 34.2L116.1 64zM28.7 144l22.9 308.7c2.5 33.4 30.3 59.3 63.8 59.3h217.1c33.5 0 61.3-25.9 63.8-59.3L419.2 144h-32.1l-22.7 306.4c-1.2 16.7-15.2 29.6-31.9 29.6H115.4c-16.8 0-30.7-12.9-31.9-29.6L60.8 144H28.7z"
			/>
		</SvgIcon>
	);
};
